import { DataGrid, type DataGridProps, type GridColDef, type GridValidRowModel } from '@mui/x-data-grid'
import { colors } from '@/theme'
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
  Badge
} from '@mui/material'
import { Add, Check, Close, FilterList } from '@mui/icons-material'
import { createPortal } from 'react-dom'
import useFilteringButtonInjection from '@/hooks/useFilteringButtonInjection'
import { useEffect, useMemo, useState } from 'react'
import { isReadOnly } from '@/lib/permissions'
import { useDebouncedCallback } from 'use-debounce'
import { TEXT_FILTER_OPERATORS, DATE_FILTER_OPERATORS, NUMBER_FILTER_OPERATORS } from '@/lib/dataGridFilters'
import type { StatusEntry } from '@/constants/statuses'

/**
 * Debounce delay for free-text / date filter inputs before the change is
 * committed to the store + URL (which triggers a refetch). Picked to feel
 * snappy while collapsing keystroke bursts (e.g. typing a DOB or a name)
 * into a single backend request — see the Apr/May 2026 perf audit.
 */
const FILTER_INPUT_DEBOUNCE_MS = 400
import useFilteringPopover, { doesOperatorRequireValue } from '@/hooks/useFilteringPopover'
import { useShallow } from 'zustand/react/shallow'
import { type GridFilterItemWithId, useFilterStore } from '@/lib/stores/filter'

type FilterFieldType = 'text' | 'date' | 'boolean' | 'number' | 'select'

interface ColumnInfo {
  field: string
  headerName: string
  type: FilterFieldType
  isDisabled: boolean
  /** Present only for `select` columns — the choices offered instead of a free-text box. */
  valueOptions?: readonly StatusEntry[]
}

export type FilterValueOptions = Record<string, readonly StatusEntry[]>

const BOOLEAN_FILTER_OPERATORS = ['is'] as const

/** Substring operators are meaningless against a fixed set of codes, so they are left out. */
const SELECT_FILTER_OPERATORS = ['equals', 'doesNotEqual', 'isAnyOf', 'isEmpty', 'isNotEmpty'] as const

/** Stable empty default, so tables without options don't rebuild their column list every render. */
const NO_FILTER_VALUE_OPTIONS: FilterValueOptions = {}

const FILTER_VALUE_WIDTH = 260

const MENU_ITEM_HEIGHT = 40
const MENU_VISIBLE_ITEMS = 6.5

const VALUE_PICKER_MENU_PROPS = {
  slotProps: {
    paper: {
      sx: {
        width: FILTER_VALUE_WIDTH,
        maxWidth: FILTER_VALUE_WIDTH,
        maxHeight: MENU_ITEM_HEIGHT * MENU_VISIBLE_ITEMS,
        // macOS hides overlay scrollbars until something scrolls, so the clipped row
        // would be the only hint. Force a persistent one to make it unambiguous.
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: colors.border.default, borderRadius: 3 }
      }
    },
    list: { sx: { py: 0 } }
  }
}

/** Human-readable labels for filter operators shown in the dropdown */
const OPERATOR_LABELS: Record<string, string> = {
  // Text operators
  contains: 'contains',
  doesNotContain: 'does not contain',
  equals: 'equals',
  doesNotEqual: 'does not equal',
  startsWith: 'starts with',
  endsWith: 'ends with',
  isEmpty: 'is empty',
  isNotEmpty: 'is not empty',
  isAnyOf: 'is any of',

  // Date operators
  is: 'is',
  isNot: 'is not',
  isAfter: 'is after',
  isOnOrAfter: 'is on or after',
  isBefore: 'is before',
  isOnOrBefore: 'is on or before',

  // Number operators
  greaterThan: 'greater than',
  greaterThanOrEqual: 'greater than or equal to',
  lessThan: 'less than',
  lessThanOrEqual: 'less than or equal to'
}

/** Returns the available filter operators based on the field type */
function getOperatorsForType(type: FilterFieldType): readonly string[] {
  switch (type) {
    case 'date':
      return DATE_FILTER_OPERATORS
    case 'boolean':
      return BOOLEAN_FILTER_OPERATORS
    case 'number':
      return NUMBER_FILTER_OPERATORS
    case 'select':
      return SELECT_FILTER_OPERATORS
    default:
      return TEXT_FILTER_OPERATORS
  }
}

/** Maps MUI DataGrid column types to our simplified FilterFieldType */
function getColumnType(column: GridColDef, hasValueOptions: boolean): FilterFieldType {
  if (hasValueOptions) return 'select'
  if (column.type === 'boolean') return 'boolean'
  if (column.type === 'date' || column.type === 'dateTime') return 'date'
  if (column.type === 'number') return 'number'
  return 'text'
}

/**
 * Collapses options that repeat a value — some statuses carry more than one label
 * (e.g. `wc-item-clinical-fan` is both "Clinical FAN" and "Clinical Further
 * Answers Needed"), and a picker needs one entry per selectable value.
 *
 * The last label wins, matching how the value→label maps in `@/constants/statuses`
 * resolve it, so the picker names a status exactly as the grid column does.
 */
function dedupeByValue(options: readonly StatusEntry[]): StatusEntry[] {
  return [...new Map(options.map(option => [option.value, option])).values()]
}

/**
 * Stands in for an unset dropdown value. Mirrors the `placeholder` the free-text input
 * shows in the same slot, so an empty filter row reads the same whichever type it is.
 */
function FilterValuePlaceholder() {
  return (
    <Box component='span' sx={{ color: colors.text.muted }}>
      Value
    </Box>
  )
}

interface FilterRowProps {
  filter: GridFilterItemWithId
  columns: ColumnInfo[]
  onUpdate: (filter: GridFilterItemWithId) => void
  onRemove: () => void
  showRemove: boolean
  excludedFilters?: string[]
}

/**
 * FilterRow - Renders a single filter row with field, operator, and value selectors.
 *
 * Each row allows the user to:
 * 1. Select which column to filter on
 * 2. Choose a comparison operator (varies by field type)
 * 3. Enter a value (input type adapts to field type: text, date picker, or boolean toggle)
 *
 * When the field changes, the operator resets to the first available for that type.
 */
function FilterRow({ filter, columns, onUpdate, onRemove, showRemove, excludedFilters = [] }: FilterRowProps) {
  const selectedColumn = columns.find(c => c.field === filter.field)
  const operators = selectedColumn ? getOperatorsForType(selectedColumn.type) : TEXT_FILTER_OPERATORS
  const needsValue = doesOperatorRequireValue(filter.operator)

  // Local mirror of the filter's value so text/date inputs echo each keystroke
  // instantly while we debounce the upstream commit (which writes to URL +
  // store and triggers an API refetch).
  const [localValue, setLocalValue] = useState<unknown>(filter.value ?? '')

  // biome-ignore lint/correctness/useExhaustiveDependencies: required.
  useEffect(() => {
    setLocalValue(filter.value ?? '')
  }, [filter.value, filter.operator, filter.field])

  const debouncedCommit = useDebouncedCallback(
    (next: unknown) => onUpdate({ ...filter, value: next }),
    FILTER_INPUT_DEBOUNCE_MS
  )

  // Selection state for `select` columns, normalised to a list: `isAnyOf` holds several
  // values, every other operator a single one. Non-select columns yield an empty list.
  const isMultiSelect = filter.operator === 'isAnyOf'
  const selectedValues: string[] = Array.isArray(filter.value)
    ? filter.value
    : typeof filter.value === 'string' && filter.value
      ? [filter.value]
      : []

  if (excludedFilters.includes(filter.field)) return null

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
      <Select
        size='small'
        value={filter.field}
        onChange={e =>
          onUpdate({
            ...filter,
            field: e.target.value,
            operator: getOperatorsForType(columns.find(c => c.field === e.target.value)?.type || 'text')[0],
            value: undefined
          })
        }
        sx={{ minWidth: 140 }}
        displayEmpty
      >
        <MenuItem value='' disabled>
          Select field
        </MenuItem>
        {columns.map(col => (
          <MenuItem key={col.field} value={col.field} disabled={col.isDisabled}>
            {col.headerName}
          </MenuItem>
        ))}
      </Select>

      <Select
        size='small'
        value={filter.operator}
        onChange={e => onUpdate({ ...filter, operator: e.target.value, value: undefined })}
        sx={{ minWidth: 140 }}
        disabled={!filter.field}
      >
        {operators.map(op => (
          <MenuItem key={op} value={op}>
            {OPERATOR_LABELS[op] || op}
          </MenuItem>
        ))}
      </Select>

      {needsValue && selectedColumn?.type === 'boolean' ? (
        <Select
          size='small'
          displayEmpty
          value={filter.value ?? ''}
          onChange={e => onUpdate({ ...filter, value: e.target.value === 'true' })}
          renderValue={selected =>
            selected === '' ? <FilterValuePlaceholder /> : selected === true || selected === 'true' ? 'Yes' : 'No'
          }
          sx={{ minWidth: 100 }}
          disabled={!filter.field}
        >
          <MenuItem value='true'>Yes</MenuItem>
          <MenuItem value='false'>No</MenuItem>
        </Select>
      ) : needsValue && selectedColumn?.type === 'select' ? (
        <Select
          size='small'
          displayEmpty
          // MUI needs the value shape to match the mode, so feed it the normalised list.
          multiple={isMultiSelect}
          value={isMultiSelect ? selectedValues : (selectedValues[0] ?? '')}
          onChange={e => onUpdate({ ...filter, value: e.target.value })}
          renderValue={() => {
            if (!selectedValues.length) return <FilterValuePlaceholder />

            return selectedValues
              .map(value => selectedColumn.valueOptions?.find(o => o.value === value)?.label ?? value)
              .join(', ')
          }}
          MenuProps={VALUE_PICKER_MENU_PROPS}
          sx={{ width: FILTER_VALUE_WIDTH, flexShrink: 0 }}
          disabled={!filter.field}
        >
          {selectedColumn.valueOptions?.map(option => {
            const isSelected = selectedValues.includes(option.value)

            return (
              <MenuItem key={option.value} value={option.value} sx={{ gap: 1, minHeight: MENU_ITEM_HEIGHT }}>
                {isMultiSelect ? (
                  <Checkbox size='small' checked={isSelected} sx={{ p: 0 }} />
                ) : (
                  <Check sx={{ fontSize: 18, visibility: isSelected ? 'visible' : 'hidden' }} />
                )}
                <Box
                  component='span'
                  title={option.label}
                  sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {option.label}
                </Box>
              </MenuItem>
            )
          })}
        </Select>
      ) : needsValue && selectedColumn?.type === 'date' ? (
        <TextField
          type='date'
          size='small'
          value={localValue ? new Date(localValue as string).toISOString().split('T')[0] : ''}
          onChange={e => {
            setLocalValue(e.target.value)
            debouncedCommit(e.target.value)
          }}
          onBlur={() => debouncedCommit.flush()}
          sx={{ minWidth: 140 }}
          disabled={!filter.field}
        />
      ) : needsValue ? (
        <TextField
          size='small'
          placeholder='Value'
          value={(localValue as string) ?? ''}
          onChange={e => {
            setLocalValue(e.target.value)
            debouncedCommit(e.target.value)
          }}
          onBlur={() => debouncedCommit.flush()}
          sx={{ minWidth: 140 }}
          disabled={!filter.field}
        />
      ) : null}

      {showRemove && (
        <IconButton size='small' onClick={onRemove} sx={{ color: colors.grey[500] }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  )
}

interface FilteringPopoverButtonProps {
  columns: readonly GridColDef[]
  tableId: string
  excludedFilters?: string[]
  filterValueOptions?: FilterValueOptions
}

/**
 * FilteringPopoverButton - The filter icon button and its associated popover panel.
 */
function FilteringPopoverButton({
  columns,
  tableId,
  excludedFilters = [],
  filterValueOptions = NO_FILTER_VALUE_OPTIONS
}: FilteringPopoverButtonProps) {
  const storeFilters = useFilterStore(useShallow(state => state.filters[tableId] ?? []))
  const columnInfos = useMemo<ColumnInfo[]>(
    () =>
      columns
        .filter(col => col.field && col.headerName)
        .map(col => {
          const valueOptions = filterValueOptions[col.field]

          return {
            field: col.field,
            headerName: col.headerName!,
            type: getColumnType(col, Boolean(valueOptions?.length)),
            isDisabled: storeFilters.some(f => f.field === col.field && col.type === 'boolean'), // If a boolean filter exists for this column, disable the select
            valueOptions: valueOptions?.length ? dedupeByValue(valueOptions) : undefined
          }
        }),
    [columns, storeFilters, filterValueOptions]
  )

  const { anchorEl, handleOpen, handleUpdateFilter, handleRemoveFilter, handleAddFilter, handleClear, handleClose } =
    useFilteringPopover({ tableId })

  const isOpen = Boolean(anchorEl)

  const filters = useMemo(
    () => storeFilters.filter(f => !excludedFilters.includes(f.field)),
    [storeFilters, excludedFilters]
  )

  return (
    <>
      <IconButton title='Edit filters' onClick={handleOpen}>
        <Badge badgeContent={filters.length} color='primary' invisible={filters.length === 0}>
          <FilterList sx={{ fontSize: 18 }} />
        </Badge>
      </IconButton>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: { p: 2, minWidth: 480, maxWidth: 640 }
          }
        }}
      >
        <Typography variant='subtitle2' sx={{ mb: 2, fontWeight: 600 }}>
          Filters
        </Typography>

        {filters.map(filter => (
          <FilterRow
            key={filter.id}
            filter={filter}
            columns={columnInfos}
            onUpdate={updated => handleUpdateFilter(filter.id, updated)}
            onRemove={() => handleRemoveFilter(filter.id)}
            showRemove={filters.length > 1}
            excludedFilters={excludedFilters}
          />
        ))}

        <Button startIcon={<Add />} onClick={handleAddFilter} size='small' sx={{ mt: 1, color: colors.brand.primary }}>
          Add filter
        </Button>

        <Box sx={{ display: 'flex', gap: 1, mt: 2, pt: 2, borderTop: `1px solid ${colors.border.default}` }}>
          <Button variant='outlined' size='small' onClick={handleClear} sx={{ flex: 1 }}>
            Clear all
          </Button>
        </Box>
      </Popover>
    </>
  )
}

type DataTableProps<R extends GridValidRowModel = any> = DataGridProps<R> &
  React.RefAttributes<HTMLDivElement> & {
    tableId?: string
    filterMode?: 'server' | 'client'
    compactMode?: boolean
  }

export default function DataGridTable<R extends GridValidRowModel = any>(
  props: DataTableProps<R> & {
    excludedFilters?: string[]
    /** Fields offered in the filter popover without being rendered as grid columns. */
    filterOnlyColumns?: readonly GridColDef[]
    /** Fixed choices for columns whose displayed label differs from the value the backend filters on. */
    filterValueOptions?: FilterValueOptions
    showQuickFilter?: boolean
  }
) {
  const {
    tableId = '',
    compactMode = false,
    excludedFilters = [],
    filterOnlyColumns = [],
    filterValueOptions,
    filterMode = compactMode ? 'client' : 'server',
    ...dataGridProps
  } = props

  // `rowCount` only applies to server-side pagination; MUI warns when it is
  // passed alongside paginationMode="client" (compact mode), so drop it there.
  if (filterMode === 'client') {
    delete dataGridProps.rowCount
  }
  const { containerRef, portalTarget } = useFilteringButtonInjection({ enabled: !compactMode })
  const readOnly = isReadOnly()

  const columns = props.columns ?? []

  return (
    <Box
      ref={containerRef}
      sx={{
        bgcolor: colors.background.paper,
        borderRadius: 2,
        border: `1px solid ${colors.border.default}`,
        overflow: 'hidden',
        py: compactMode ? 0 : 3,
        position: 'relative'
      }}
    >
      <DataGrid
        pageSizeOptions={[10, 25, 50]}
        disableColumnMenu
        disableRowSelectionOnClick
        autoHeight
        rowHeight={compactMode ? 41 : 96}
        paginationMode={filterMode}
        filterMode={filterMode}
        sortingMode={filterMode}
        showToolbar={!compactMode}
        sx={{
          '& [aria-label="Filters"]': { display: 'none' }
        }}
        {...dataGridProps}
        {...(compactMode && {
          hideFooter: true,
          disableColumnSorting: true,
          disableColumnFilter: true
        })}
        {...(readOnly && { isCellEditable: () => false })}
        slotProps={{
          toolbar: {
            showQuickFilter: props.showQuickFilter
          }
        }}
      />
      {!compactMode &&
        portalTarget &&
        createPortal(
          <FilteringPopoverButton
            columns={[...columns, ...filterOnlyColumns]}
            tableId={tableId}
            excludedFilters={excludedFilters}
            filterValueOptions={filterValueOptions}
          />,
          portalTarget
        )}
    </Box>
  )
}
